import { useState, useEffect, useCallback } from 'react';
import {
  generateRepurpose,
  saveRepurpose,
  listRepurpose,
  REPURPOSE_PLATFORMS,
  type ContentProject,
  type ContentScene,
  type RepurposeResultData,
  type RepurposePlatformData,
} from '@/lib/contentStudio';

interface Props {
  project: ContentProject;
  scenes: ContentScene[];
}

const SHORT_TARGETS = ['60', '30', '15'];

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* presse-papiers indisponible */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Copier"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className={done ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'}></i>
      {done ? 'Copié' : 'Copier'}
    </button>
  );
}

/**
 * Repurposing réseaux sociaux (specs 40 & 69) : à partir de la vidéo, l'IA
 * produit pour chaque plateforme un titre, une description et des hashtags,
 * ainsi que des versions courtes (Shorts / Reels / TikTok) liées au projet.
 */
export default function RepurposePanel({ project, scenes }: Props) {
  const [result, setResult] = useState<RepurposeResultData | null>(null);
  const [activePlatform, setActivePlatform] = useState<string>('youtube');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await listRepurpose(project.id);
      if (rows.length === 0) return;
      const platforms: Record<string, RepurposePlatformData> = {};
      const shorts: Record<string, number[]> = {};
      let hook = '';
      for (const r of rows) {
        if (r.platform === 'short') {
          if (r.short_seconds) shorts[String(r.short_seconds)] = r.scenes || [];
        } else {
          platforms[r.platform] = { title: r.title || '', description: r.description || '', hashtags: r.hashtags || [] };
        }
        if (r.hook) hook = r.hook;
      }
      setResult({ hook, platforms, shorts });
    } catch {
      /* pas bloquant */
    } finally {
      setLoaded(true);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await generateRepurpose({
        title: project.name,
        script: project.script || '',
        scenes: scenes.map((s) => ({ number: s.number, script: s.script })),
        language: project.language,
        style: project.style || undefined,
      });
      setResult(res);
      // Persistance (titres/descriptions/hashtags + versions courtes).
      const rows: { platform: string; title?: string; description?: string; hashtags?: string[]; hook?: string; short_seconds?: number | null; scenes?: number[] }[] =
        REPURPOSE_PLATFORMS.map((p) => ({
          platform: p.key,
          title: res.platforms[p.key]?.title,
          description: res.platforms[p.key]?.description,
          hashtags: res.platforms[p.key]?.hashtags,
          hook: res.hook,
        }));
      for (const sec of SHORT_TARGETS) {
        rows.push({
          platform: 'short',
          short_seconds: Number(sec),
          scenes: res.shorts[sec] || [],
          title: `Version ${sec}s`,
          hook: res.hook,
        });
      }
      await saveRepurpose(project.id, rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la génération');
    } finally {
      setBusy(false);
    }
  };

  const current = result?.platforms[activePlatform] || null;

  const shortDuration = (numbers: number[]): number =>
    numbers.reduce((acc, n) => acc + (scenes.find((s) => s.number === n)?.duration || 0), 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-share-forward-line text-accent-500"></i>Réseaux sociaux
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Titres, descriptions et hashtags adaptés à chaque plateforme, plus des versions courtes (Shorts / Reels / TikTok) liées à ce projet.
          </p>
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
          {busy ? 'Génération…' : result ? 'Régénérer' : 'Générer les métadonnées'}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {busy && !result && (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      )}

      {!busy && !result && loaded && (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-share-forward-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucune déclinaison réseau</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">Générez vos titres, descriptions et hashtags pour toutes les plateformes.</p>
        </div>
      )}

      {result && (
        <>
          {result.hook && (
            <div className="mb-4 bg-accent-50 border border-accent-200/70 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <i className="ri-lightbulb-flash-line text-accent-600 mt-0.5"></i>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-accent-800 uppercase tracking-wider mb-0.5">Hook principal</p>
                  <p className="text-sm text-foreground-800">{result.hook}</p>
                </div>
                <CopyButton text={result.hook} />
              </div>
            </div>
          )}

          {/* Onglets plateformes */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-4 overflow-x-auto">
            {REPURPOSE_PLATFORMS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setActivePlatform(p.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activePlatform === p.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={p.icon}></i>{p.label}
              </button>
            ))}
          </div>

          {current ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Titre</label>
                  <CopyButton text={current.title} />
                </div>
                <p className="text-sm font-medium text-foreground-900">{current.title || '—'}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Description / caption</label>
                  <CopyButton text={current.description} />
                </div>
                <p className="text-sm text-foreground-700 whitespace-pre-wrap">{current.description || '—'}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Hashtags</label>
                  <CopyButton text={(current.hashtags || []).join(' ')} />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(current.hashtags || []).length === 0 && <span className="text-sm text-foreground-400">—</span>}
                  {(current.hashtags || []).map((h) => (
                    <span key={h} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-medium">{h}</span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground-400 py-6 text-center">Aucune donnée pour cette plateforme.</p>
          )}

          {/* Versions courtes */}
          <div className="mt-6">
            <h4 className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-3">
              <i className="ri-scissors-cut-line text-foreground-500"></i>Versions courtes (Shorts / Reels / TikTok)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SHORT_TARGETS.map((sec) => {
                const numbers = result.shorts[sec] || [];
                const total = shortDuration(numbers);
                return (
                  <div key={sec} className="bg-background-50 border border-background-200/70 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-foreground-900">Version {sec} s</span>
                      <span className="text-[11px] text-foreground-500">{total ? `~${total}s` : ''}</span>
                    </div>
                    {numbers.length === 0 ? (
                      <p className="text-xs text-foreground-400">Aucune scène retenue.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {numbers.map((n) => (
                          <span key={n} className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold">Scène {String(n).padStart(2, '0')}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}